/**
 * HostedAgentStatusBadge — status indicator for hosted agents.
 */

import { Badge, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import type { HostedAgentStatus } from '@/types/hostedAgent';

const useStyles = makeStyles({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    // Focusable so the descriptive tooltip is reachable by keyboard (mirrors the
    // :focus-visible treatment on ConnectionStatusBadge).
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
    },
  },
});

const statusConfig: Record<HostedAgentStatus, { color: 'success' | 'warning' | 'danger' | 'informative' | 'important'; label: string; tooltip: string }> = {
  connected: { color: 'success', label: 'Connected', tooltip: 'Agent is healthy and all auth is working' },
  provisioning: { color: 'informative', label: 'Provisioning', tooltip: 'Agent is being set up — RBAC propagation may take a few minutes' },
  auth_failed: { color: 'danger', label: 'Auth Failed', tooltip: 'Logic App managed identity cannot reach Foundry' },
  agent_error: { color: 'danger', label: 'Agent Error', tooltip: 'Agent is reachable but OpenAI auth is failing' },
  version_mismatch: { color: 'warning', label: 'Update Available', tooltip: 'Running image does not match expected version' },
  unreachable: { color: 'danger', label: 'Unreachable', tooltip: 'Agent version is not active or container is down' },
  unknown: { color: 'important', label: 'Unknown', tooltip: 'Agent status has not been checked yet' },
};

interface HostedAgentStatusBadgeProps {
  status: HostedAgentStatus;
}

export const HostedAgentStatusBadge: React.FC<HostedAgentStatusBadgeProps> = ({ status }) => {
  const styles = useStyles();
  const config = statusConfig[status] ?? statusConfig.unknown;

  return (
    <Tooltip content={config.tooltip} relationship="description">
      <Badge className={styles.badge} color={config.color} appearance="filled" tabIndex={0}>
        {config.label}
      </Badge>
    </Tooltip>
  );
};
